import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

/**
 * Hooks for `backend/app_core/routers/tiktok.py`. Covers the two
 * endpoints consumed by the accounts page (T3.1):
 *   - `GET  /auth/tiktok/accounts`  — list connected advertiser accounts
 *     with inline balance + adgroup quota.
 *   - `POST /auth/tiktok/refresh`   — trigger a balance refresh across
 *     the user's accounts.
 *
 * Deviation note: neither handler declares a Pydantic `response_model`
 * (they return hand-built `JSONResponse`s), so the generated OpenAPI
 * schema type for the body is `unknown`. We therefore declare explicit
 * shapes mirroring `tiktok.py`. Mirrors the pattern in `sync.ts`.
 *
 * TODO: replace with paths[...] once GET /auth/tiktok/accounts has a
 *       response_model (and same for POST /auth/tiktok/refresh).
 */

export interface TikTokAccountQuota {
  used?: number | null
  total_available?: number | null
  remaining?: number | null
}

export interface TikTokAccount {
  advertiser_id: string
  advertiser_name: string
  /** TikTok /advertiser/info/ status (cached on balance refresh). */
  advertiser_status?: string | null
  is_active?: boolean
  deleted_at?: string | null
  balance?: number | string | null
  currency?: string | null
  /** Aggregated TikTok spend from synced daily metrics (last 7 UTC days). */
  spend_7d?: number | null
  quota?: TikTokAccountQuota | null
}

export interface TikTokAccountsResponse {
  success: boolean
  accounts: TikTokAccount[]
}

export interface TikTokRefreshResponse {
  success: boolean
  updated_count?: number
  bc_budget_applied?: number
  bc_count?: number
  bc_errors?: string[]
  message?: string
  detail?: string
}

export const TIKTOK_QUERY_KEYS = {
  all: ["tiktok"] as const,
  accounts: ["tiktok", "accounts"] as const,
} as const

export function useTikTokAccounts(options?: {
  includeDeleted?: boolean
  /** Ads Manager filter: only accounts with metrics in the date window. */
  activeInRange?: boolean
  startDate?: string
  endDate?: string
  alwaysIncludeAdvertiserId?: string
}) {
  const includeDeleted = options?.includeDeleted ?? true
  const activeInRange = options?.activeInRange ?? false
  const startDate = options?.startDate
  const endDate = options?.endDate
  const alwaysIncludeAdvertiserId = options?.alwaysIncludeAdvertiserId

  return useQuery<TikTokAccountsResponse>({
    queryKey: [
      ...TIKTOK_QUERY_KEYS.accounts,
      includeDeleted,
      activeInRange,
      startDate,
      endDate,
      alwaysIncludeAdvertiserId,
    ] as const,
    queryFn: () => {
      const params = new URLSearchParams()
      params.set("include_deleted", includeDeleted ? "true" : "false")
      if (activeInRange && startDate && endDate) {
        params.set("active_in_range", "true")
        params.set("start_date", startDate)
        params.set("end_date", endDate)
      }
      if (alwaysIncludeAdvertiserId) {
        params.set("always_include_advertiser_id", alwaysIncludeAdvertiserId)
      }
      return apiFetch<TikTokAccountsResponse>(
        `/auth/tiktok/accounts?${params.toString()}`,
      )
    },
    enabled: !activeInRange || Boolean(startDate && endDate),
    retry: false,
  })
}

export function useRefreshTikTokBalances() {
  const qc = useQueryClient()
  return useMutation<TikTokRefreshResponse>({
    mutationFn: () =>
      apiFetch<TikTokRefreshResponse>("/auth/tiktok/refresh", {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TIKTOK_QUERY_KEYS.accounts })
    },
  })
}
